import { configureStore, combineReducers } from '@reduxjs/toolkit';
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import userReducer from './slices/userSlice';
import authReducer from './slices/authSlice';
import onboardingReducer from './slices/onboardingSlice';
import apiConfigReducer from './slices/apiSlice';
import { baseApi } from '@/shared/lib/baseApi';
import { tokenApi } from '@/features/auth/api/tokenApi';
import roleReducer from './slices/roleSlice';
import permissionReducer from './slices/permissionSlice';
import userManagementReducer from './slices/userManagementSlice';
import enterpriseReducer from './slices/enterpriseSlice';
import slcDetailsReducer from './slices/slcDetailsSlice';

// Redux persist configuration
const persistConfig = {
  key: 'root',
  version: 2, // Incremented to clear old cached state without enterpriseId
  storage,
  whitelist: ['user', 'auth'],
};

// Combine reducers
export const rootReducer = combineReducers({
  user: userReducer,
  auth: authReducer,
  onboarding: onboardingReducer,
  apiConfig: apiConfigReducer,
  role: roleReducer,
  permission: permissionReducer,
  userManagement: userManagementReducer,
  enterprise: enterpriseReducer,
  slcDetails: slcDetailsReducer,
  // Add base RTK Query API reducer - all injected endpoints share this
  [baseApi.reducerPath]: baseApi.reducer,
  // Add tokenApi reducer for token refresh operations
  [tokenApi.reducerPath]: tokenApi.reducer,
});

// Create persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Configure store
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .concat(baseApi.middleware) // Add base API middleware
      .concat(tokenApi.middleware), // Add tokenApi middleware
});

// Create persistor
export const persistor = persistStore(store);

// Export types
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
